import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence, useScroll, useMotionValueEvent, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FiSun, FiMoon } from 'react-icons/fi';
import { useTheme } from '../contexts/ThemeContext';
import Logo from './Logo';

// Premium glass effect header with smooth scroll transitions
const Header = styled(motion.header)`
  position: fixed;
  top: 0;
  inset-inline: 0;
  z-index: 1000;
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border-bottom: 1px solid ${({ theme, $scrolled }) =>
    $scrolled
      ? theme.colors.divider
      : 'transparent'};
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  /* Transparent when at top */
  background: ${({ theme, $scrolled }) =>
    $scrolled
      ? `${theme.colors.surface}f8`
      : `${theme.colors.surface}40`};

  /* Shadow when scrolled */
  box-shadow: ${({ $scrolled }) =>
    $scrolled
      ? '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)'
      : 'none'};
`;

const Wrapper = styled(motion.div)`
  max-width: ${({ theme }) => theme.layout.maxWidth};
  margin: 0 auto;
  padding: ${({ $scrolled }) => $scrolled ? '12px 24px' : '16px 24px'};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.layout.radiusMd};
  min-width: 0;
  transition: padding 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: ${({ $scrolled }) => $scrolled ? '10px 16px' : '12px 16px'};
    gap: ${({ theme }) => theme.layout.radiusSm};
  }
`;

const NavLinks = styled.nav`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.layout.radiusLg};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: none;
  }
`;

const NavLink = styled(motion(Link))`
  font-family: ${({ theme }) => theme.fonts.ar};
  font-weight: 500;
  font-size: 0.95rem;
  letter-spacing: 0.01em;
  color: ${({ theme, $active }) =>
    $active
      ? theme.colors.primary
      : theme.colors.textDark};
  position: relative;
  padding: 8px 4px;
  text-decoration: none;
  transition: color 0.3s ease;
  display: flex;
  align-items: center;

  /* Active indicator underline */
  &::after {
    content: '';
    position: absolute;
    inset-inline: 0;
    bottom: 0;
    height: 2.5px;
    background: ${({ theme }) => theme.colors.secondary};
    transform: scaleX(${({ $active }) => $active ? 1 : 0});
    transform-origin: center;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border-radius: 2px;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }

  &:hover::after {
    transform: scaleX(1);
  }

  /* Focus visible for accessibility */
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.secondary};
    outline-offset: 4px;
    border-radius: ${({ theme }) => theme.layout.radiusSm};
  }
`;

const MobileMenuButton = styled(motion.button)`
  display: none;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.textLight};
  border: none;
  border-radius: ${({ theme }) => theme.layout.radiusSm};
  font-size: 1.25rem;
  cursor: pointer;
  transition: all 0.2s ease;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: flex;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.secondary};
    outline-offset: 2px;
  }
`;

// Overlay behind mobile menu
const MobileOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 999;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: none;
  }
`;

const MobileNav = styled(motion.nav)`
  position: fixed;
  top: 72px;
  inset-inline: ${({ theme }) => theme.layout.radiusMd};
  max-height: calc(100vh - 100px);
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.layout.radiusLg};
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  padding: ${({ theme }) => theme.layout.radiusLg};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.layout.radiusSm};
  overflow-y: auto;
  z-index: 1000;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: none;
  }
`;

const MobileNavLink = styled(motion(Link))`
  font-family: ${({ theme }) => theme.fonts.ar};
  font-weight: 600;
  font-size: 1.1rem;
  color: ${({ theme, $active }) =>
    $active
      ? theme.colors.primary
      : theme.colors.textDark};
  padding: ${({ theme }) => theme.layout.radiusMd};
  border-radius: ${({ theme }) => theme.layout.radiusMd};
  text-decoration: none;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset-inline-start: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 4px;
    height: 0;
    background: ${({ theme }) => theme.colors.secondary};
    border-radius: 0 2px 2px 0;
    transition: height 0.3s ease;
  }

  ${({ $active }) => $active && `
    background: ${({ theme }) => theme.colors.backgroundAlt};
    &::before {
      height: 60%;
    }
  `}

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundAlt};
    color: ${({ theme }) => theme.colors.primary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.secondary};
    outline-offset: 2px;
    border-radius: ${({ theme }) => theme.layout.radiusMd};
  }
`;

const LanguageSwitcher = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.layout.radiusSm};
  align-items: center;
  margin-inline-start: ${({ theme }) => theme.layout.radiusLg};
  padding-inline-start: ${({ theme }) => theme.layout.radiusLg};
  border-inline-start: 1px solid ${({ theme }) => theme.colors.divider};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    margin-inline-start: 0;
    margin-top: ${({ theme }) => theme.layout.radiusMd};
    padding-inline-start: 0;
    padding-top: ${({ theme }) => theme.layout.radiusMd};
    border-inline-start: none;
    border-top: 1px solid ${({ theme }) => theme.colors.divider};
  }
`;

const LangButton = styled(motion.button)`
  padding: 6px 14px;
  border: 1.5px solid ${({ theme, $active }) =>
    $active
      ? theme.colors.primary
      : theme.colors.outline};
  background: ${({ theme, $active }) =>
    $active
      ? theme.colors.primary
      : 'transparent'};
  color: ${({ theme, $active }) =>
    $active
      ? theme.colors.textLight
      : theme.colors.textDark};
  border-radius: ${({ theme }) => theme.layout.radiusSm};
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 44px;
  height: 36px;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme, $active }) =>
      $active
        ? theme.colors.primary
        : `${theme.colors.primary}15`};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.secondary};
    outline-offset: 2px;
  }
`;

const ThemeToggle = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1.5px solid ${({ theme }) => theme.colors.outline};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.textDark};
  border-radius: ${({ theme }) => theme.layout.radiusSm};
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  margin-inline-start: ${({ theme }) => theme.layout.radiusSm};
  font-size: 1.2rem;

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundAlt};
    border-color: ${({ theme }) => theme.colors.secondary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.secondary};
    outline-offset: 2px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    margin-inline-start: 0;
    margin-top: ${({ theme }) => theme.layout.radiusSm};
  }
`;

const Navbar = ({ menuOpen, setMenuOpen }) => {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();
  const { isDarkMode, toggleTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const { scrollY } = useScroll();
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname, setMenuOpen]);

  // Handle scroll with smooth threshold
  useMotionValueEvent(scrollY, 'change', (latest) => {
    setScrolled(latest > 80);
  });

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && menuOpen) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [menuOpen, setMenuOpen]);

  const navItems = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') },
    { path: '/projects', label: t('nav.projects') },
    { path: '/services', label: t('nav.services') },
    { path: '/contact', label: t('nav.contact') },
  ];

  const languages = [
    { code: 'ar', label: t('common.languages.ar') },
    { code: 'en', label: t('common.languages.en') },
    { code: 'tr', label: t('common.languages.tr') },
  ];

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
  };

  return (
    <>
      <Header
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{
          duration: prefersReducedMotion ? 0 : 0.5,
          ease: [0.4, 0, 0.2, 1]
        }}
        $scrolled={scrolled}
      >
        <Wrapper
          $scrolled={scrolled}
          initial={false}
          animate={{ padding: scrolled ? '12px 24px' : '16px 24px' }}
          transition={{ duration: 0.4 }}
        >
          {/* Logo on right (RTL) */}
          <Logo />

          {/* Navigation links on left */}
          <NavLinks aria-label="روابط الموقع">
            {navItems.map((item, index) => (
              <NavLink
                key={item.path}
                to={item.path}
                $active={pathname === item.path}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  delay: prefersReducedMotion ? 0 : 0.1 + index * 0.05,
                  duration: prefersReducedMotion ? 0 : 0.3,
                }}
                whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
                whileTap={prefersReducedMotion ? {} : { scale: 0.98 }}
              >
                {item.label}
              </NavLink>
            ))}

            {/* Language & Theme Switcher */}
            <LanguageSwitcher>
              {languages.map((lang) => (
                <LangButton
                  key={lang.code}
                  type="button"
                  $active={i18n.language === lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  aria-label={`Switch to ${lang.label}`}
                  whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
                  whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
                >
                  {lang.code.toUpperCase()}
                </LangButton>
              ))}
              <ThemeToggle
                type="button"
                onClick={toggleTheme}
                aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                title={isDarkMode ? 'الوضع النهاري' : 'الوضع الليلي'}
                whileHover={prefersReducedMotion ? {} : { scale: 1.05, rotate: 5 }}
                whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
              >
                {isDarkMode ? <FiSun /> : <FiMoon />}
              </ThemeToggle>
            </LanguageSwitcher>
          </NavLinks>

          {/* Mobile Menu Button */}
          <MobileMenuButton
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
            whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
          >
            {menuOpen ? '✕' : '☰'}
          </MobileMenuButton>
        </Wrapper>
      </Header>

      {/* Mobile Menu Overlay & Drawer */}
      <AnimatePresence>
        {menuOpen && (
          <>
            <MobileOverlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setMenuOpen(false)}
              aria-hidden="true"
            />
            <MobileNav
              id="mobile-menu"
              initial={{ opacity: 0, x: '100%' }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: '100%' }}
              transition={{
                duration: prefersReducedMotion ? 0 : 0.3,
                ease: [0.4, 0, 0.2, 1],
              }}
              role="navigation"
              aria-label="Mobile navigation"
            >
              {navItems.map((item, index) => (
                <MobileNavLink
                  key={item.path}
                  to={item.path}
                  $active={pathname === item.path}
                  onClick={() => setMenuOpen(false)}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    delay: prefersReducedMotion ? 0 : index * 0.05,
                    duration: prefersReducedMotion ? 0 : 0.2,
                  }}
                  whileHover={prefersReducedMotion ? {} : { x: -4 }}
                >
                  {item.label}
                </MobileNavLink>
              ))}
              <LanguageSwitcher>
                {languages.map((lang) => (
                  <LangButton
                    key={lang.code}
                    type="button"
                    $active={i18n.language === lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    aria-label={`Switch to ${lang.label}`}
                    whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
                    whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
                  >
                    {lang.code.toUpperCase()}
                  </LangButton>
                ))}
                <ThemeToggle
                  type="button"
                  onClick={toggleTheme}
                  aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                  title={isDarkMode ? 'الوضع النهاري' : 'الوضع الليلي'}
                  whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
                  whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
                >
                  {isDarkMode ? <FiSun /> : <FiMoon />}
                </ThemeToggle>
              </LanguageSwitcher>
            </MobileNav>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
